import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TablerIconsModule } from 'angular-tabler-icons';
import { Subscription } from 'rxjs';
import {
  AppDialogDismissReason,
  AppDialogIcon,
  AppDialogOptions,
  AppDialogResult,
  appDialogAllows,
  appDialogConfirmValue,
  appDialogIcon,
  appDialogInitialInputValue,
  appDialogPreConfirmOutcome,
} from 'src/app/core/services/app-dialog/app-dialog-rules';

const ICON_NAMES: Record<AppDialogIcon, string> = {
  success: 'circle-check',
  error: 'alert-circle',
  warning: 'alert-triangle',
  info: 'info-circle',
  question: 'help-circle',
};

/**
 * The single application dialog, opened through AppDialog.fire(). See app-dialog-rules.ts.
 *
 * Not encapsulated on purpose: `html` is markup written by the pages (forms with legacy
 * `app-dialog-input` classes, emphasis, tables), injected with [innerHTML], so the styles that
 * format it must reach it. Every rule is scoped under `.app-dialog`.
 */
@Component({
  selector: 'app-dialog',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, MatButtonModule, MatProgressSpinnerModule, TablerIconsModule],
  template: `
    <div #popup class="app-dialog" [ngClass]="options.customClass?.popup || ''" [style.background]="options.background || null">
      <button
        *ngIf="options.showCloseButton"
        type="button"
        class="app-dialog-close"
        aria-label="Close"
        (click)="dismiss(reasons.close)">
        <i-tabler name="x" class="icon-18"></i-tabler>
      </button>

      <div class="app-dialog-header" *ngIf="title || icon">
        <i-tabler *ngIf="icon" [name]="iconName" class="icon-26 app-dialog-icon" [ngClass]="'app-dialog-icon--' + icon"></i-tabler>
        <h2 class="app-dialog-title" [ngClass]="options.customClass?.title || ''" *ngIf="title">{{ title }}</h2>
      </div>

      <div class="app-dialog-body">
        <img
          *ngIf="options.imageUrl"
          class="app-dialog-image"
          [src]="options.imageUrl"
          [alt]="options.imageAlt || ''"
          [style.width]="size(options.imageWidth)"
          [style.height]="size(options.imageHeight)" />

        <div
          #htmlContainer
          class="app-dialog-html"
          [ngClass]="options.customClass?.htmlContainer || ''"
          *ngIf="html || options.text">
          <div *ngIf="html" [innerHTML]="html"></div>
          <p *ngIf="!html && options.text" class="app-dialog-text">{{ options.text }}</p>
        </div>

        <ng-container *ngIf="options.input as type">
          <label class="app-dialog-input-label" *ngIf="options.inputLabel">{{ options.inputLabel }}</label>

          <textarea
            *ngIf="type === 'textarea'"
            #inputEl
            class="app-dialog-input app-dialog-textarea"
            [ngClass]="options.customClass?.input || ''"
            [placeholder]="options.inputPlaceholder || ''"
            [(ngModel)]="inputValue"
            (ngModelChange)="onInput()"></textarea>

          <select
            *ngIf="type === 'select'"
            #inputEl
            class="app-dialog-input app-dialog-select"
            [ngClass]="options.customClass?.input || ''"
            [(ngModel)]="inputValue"
            (ngModelChange)="onInput()">
            <option *ngIf="options.inputPlaceholder" value="" disabled>{{ options.inputPlaceholder }}</option>
            <option *ngFor="let option of inputOptions" [value]="option.key">{{ option.label }}</option>
          </select>

          <div *ngIf="type === 'radio'" class="app-dialog-radio">
            <label *ngFor="let option of inputOptions">
              <input type="radio" name="app-dialog-radio" [value]="option.key" [(ngModel)]="inputValue" (ngModelChange)="onInput()" />
              {{ option.label }}
            </label>
          </div>

          <label *ngIf="type === 'checkbox'" class="app-dialog-checkbox">
            <input #inputEl type="checkbox" [(ngModel)]="inputValue" (ngModelChange)="onInput()" />
            {{ options.inputPlaceholder }}
          </label>

          <input
            *ngIf="isPlainInput(type)"
            #inputEl
            class="app-dialog-input"
            [ngClass]="options.customClass?.input || ''"
            [type]="type"
            [placeholder]="options.inputPlaceholder || ''"
            [(ngModel)]="inputValue"
            (ngModelChange)="onInput()" />
        </ng-container>

        <div class="app-dialog-validation" *ngIf="validationMessage()" role="alert">
          <i-tabler name="alert-circle" class="icon-16"></i-tabler>
          <span>{{ validationMessage() }}</span>
        </div>

        <div class="app-dialog-footer" *ngIf="options.footer" [innerHTML]="footer"></div>
      </div>

      <div class="app-dialog-actions" [ngClass]="options.customClass?.actions || ''" *ngIf="hasButtons || loading()">
        <mat-spinner *ngIf="loading()" diameter="28"></mat-spinner>
        <ng-container *ngIf="!loading()">
          <button
            *ngIf="options.showCancelButton"
            #cancelBtn
            mat-stroked-button
            type="button"
            [ngClass]="buttonClass('cancel')"
            [style.background-color]="options.cancelButtonColor || null"
            [style.color]="options.cancelButtonColor ? '#fff' : null"
            (click)="dismiss(reasons.cancel)">
            {{ options.cancelButtonText || 'Cancel' }}
          </button>
          <button
            *ngIf="options.showDenyButton"
            #denyBtn
            mat-flat-button
            type="button"
            [ngClass]="buttonClass('deny')"
            [style.background-color]="options.denyButtonColor || null"
            (click)="deny()">
            {{ options.denyButtonText || 'No' }}
          </button>
          <button
            *ngIf="options.showConfirmButton !== false"
            #confirmBtn
            mat-flat-button
            type="button"
            [ngClass]="buttonClass('confirm')"
            [style.background-color]="options.confirmButtonColor || null"
            (click)="confirm()">
            {{ options.confirmButtonText || 'OK' }}
          </button>
        </ng-container>
      </div>

      <div class="app-dialog-timer" *ngIf="options.timer && options.timerProgressBar">
        <div class="app-dialog-timer-bar" [style.animation-duration.ms]="options.timer"></div>
      </div>
    </div>
  `,
  styles: [`
    .app-dialog-panel .mat-mdc-dialog-surface { border-radius: 14px; }
    .app-dialog { position: relative; padding: 22px 24px 18px; color: #374151; }
    .app-dialog-close {
      position: absolute; top: 10px; right: 10px; border: 0; background: transparent;
      color: #6b7280; cursor: pointer; padding: 4px; border-radius: 6px; line-height: 0;
    }
    .app-dialog-close:hover { background: #f3f4f6; }
    .app-dialog-header { display: flex; align-items: center; gap: 10px; margin: 0 24px 10px 0; }
    .app-dialog-title { margin: 0; font-size: 1.2rem; font-weight: 600; color: #111827; line-height: 1.35; }
    .app-dialog-icon { flex: 0 0 auto; }
    .app-dialog-icon--success { color: #13deb9; }
    .app-dialog-icon--error { color: #fa896b; }
    .app-dialog-icon--warning { color: #ffae1f; }
    .app-dialog-icon--info, .app-dialog-icon--question { color: #5d87ff; }
    .app-dialog-body { max-height: 70vh; overflow: auto; }
    .app-dialog-html { line-height: 1.5; word-break: break-word; }
    .app-dialog-text { margin: 0; white-space: pre-line; }
    .app-dialog-image { display: block; max-width: 100%; margin: 0 auto 12px; }
    .app-dialog-input-label { display: block; margin: 12px 0 4px; font-weight: 500; color: #111827; }
    .app-dialog .app-dialog-input {
      display: block; box-sizing: border-box; width: 100%; min-height: 40px; margin: 8px 0;
      padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 8px; background: #fff;
      color: #111827; font: inherit; font-size: 14px;
    }
    .app-dialog .app-dialog-input:focus { outline: none; border-color: #5d87ff; box-shadow: 0 0 0 3px rgba(93, 135, 255, 0.18); }
    .app-dialog .app-dialog-textarea { min-height: 96px; resize: vertical; }
    .app-dialog-radio { display: flex; flex-direction: column; gap: 4px; margin-top: 8px; }
    .app-dialog-radio label, .app-dialog-checkbox { display: flex; align-items: center; gap: 8px; padding: 6px 8px; border-radius: 6px; cursor: pointer; }
    .app-dialog-radio label:hover { background: #f0f4f8; }
    .app-dialog-validation {
      display: flex; align-items: center; gap: 6px; margin-top: 10px; padding: 8px 12px;
      border-radius: 8px; background: #fef2f2; color: #991b1b; font-size: 13px;
    }
    .app-dialog-footer { margin-top: 14px; padding-top: 10px; border-top: 1px solid #e5e7eb; font-size: 13px; color: #6b7280; }
    .app-dialog-actions { display: flex; justify-content: flex-end; align-items: center; flex-wrap: wrap; gap: 8px; margin-top: 18px; min-height: 36px; }
    .app-dialog-btn-default { border-radius: 999px !important; padding: 0 20px !important; }
    .app-dialog-timer { position: absolute; left: 0; right: 0; bottom: 0; height: 3px; background: #eef2ff; overflow: hidden; border-radius: 0 0 14px 14px; }
    .app-dialog-timer-bar { height: 100%; background: #5d87ff; animation-name: app-dialog-timer; animation-timing-function: linear; animation-fill-mode: forwards; }
    @keyframes app-dialog-timer { from { width: 100%; } to { width: 0; } }

    .dark-theme .app-dialog { color: rgba(255, 255, 255, 0.78); }
    .dark-theme .app-dialog-title, .dark-theme .app-dialog-input-label { color: rgba(255, 255, 255, 0.92); }
    .dark-theme .app-dialog .app-dialog-input { background: rgba(255, 255, 255, 0.04); border-color: rgba(255, 255, 255, 0.16); color: rgba(255, 255, 255, 0.92); }
    .dark-theme .app-dialog-footer { border-top-color: rgba(255, 255, 255, 0.12); }
  `],
})
export class AppDialogComponent implements AfterViewInit, OnDestroy {
  readonly reasons = AppDialogDismissReason;
  readonly loading = signal(false);
  readonly validationMessage = signal<string | null>(null);

  readonly title: string;
  readonly icon: AppDialogIcon | null;
  readonly iconName: string;
  readonly html: SafeHtml | null;
  readonly footer: SafeHtml | null;
  readonly inputOptions: { key: string; label: string }[];
  inputValue: any;

  @ViewChild('popup') popupRef?: ElementRef<HTMLElement>;
  @ViewChild('htmlContainer') htmlContainerRef?: ElementRef<HTMLElement>;
  @ViewChild('inputEl') inputRef?: ElementRef<HTMLElement>;
  @ViewChild('confirmBtn') confirmBtnRef?: ElementRef<HTMLElement>;
  @ViewChild('denyBtn') denyBtnRef?: ElementRef<HTMLElement>;
  @ViewChild('cancelBtn') cancelBtnRef?: ElementRef<HTMLElement>;

  private timerHandle: ReturnType<typeof setTimeout> | null = null;
  private timerEndsAt = 0;
  private closing = false;
  private readonly subscriptions = new Subscription();

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly options: AppDialogOptions,
    private readonly dialogRef: MatDialogRef<AppDialogComponent, AppDialogResult>,
    sanitizer: DomSanitizer
  ) {
    this.title = options.title ?? options.titleText ?? '';
    this.icon = appDialogIcon(options.icon);
    this.iconName = this.icon ? ICON_NAMES[this.icon] : '';
    // Page-authored markup, often a form (<input>, <select>) that Angular's sanitizer would strip.
    // Same trust as the former alert library gave it: dynamic values must be escaped by the page.
    this.html = options.html ? sanitizer.bypassSecurityTrustHtml(options.html) : null;
    this.footer = options.footer ? sanitizer.bypassSecurityTrustHtml(options.footer) : null;
    this.inputOptions = Object.entries(options.inputOptions ?? {}).map(([key, label]) => ({ key, label: String(label) }));
    this.inputValue = appDialogInitialInputValue(options.input, options.inputValue);

    this.subscriptions.add(
      dialogRef.backdropClick().subscribe(() => {
        if (!this.loading() && appDialogAllows(options.allowOutsideClick, true)) {
          this.dismiss(AppDialogDismissReason.backdrop);
        }
      })
    );
    this.subscriptions.add(
      dialogRef.keydownEvents().subscribe((event) => {
        if (event.key === 'Escape') {
          if (!this.loading() && appDialogAllows(options.allowEscapeKey, true)) {
            event.preventDefault();
            this.dismiss(AppDialogDismissReason.esc);
          }
          return;
        }
        if (event.key === 'Enter' && this.shouldConfirmOnEnter(event)) {
          event.preventDefault();
          this.confirm();
        }
      })
    );
  }

  get hasButtons(): boolean {
    return this.options.showConfirmButton !== false || !!this.options.showCancelButton || !!this.options.showDenyButton;
  }

  ngAfterViewInit(): void {
    if (this.options.timer && this.options.timer > 0) {
      this.timerEndsAt = Date.now() + this.options.timer;
      this.timerHandle = setTimeout(() => this.dismiss(AppDialogDismissReason.timer), this.options.timer);
    }
    const input = this.inputRef?.nativeElement;
    if (input) {
      Object.entries(this.options.inputAttributes ?? {}).forEach(([name, value]) => input.setAttribute(name, String(value)));
      setTimeout(() => input.focus());
    }
  }

  ngOnDestroy(): void {
    this.clearTimer();
    this.subscriptions.unsubscribe();
  }

  // ── Controls used by AppDialog (the static facade) ─────────────────────────

  get popupElement(): HTMLElement | null {
    return this.popupRef?.nativeElement ?? null;
  }

  get htmlContainerElement(): HTMLElement | null {
    return this.htmlContainerRef?.nativeElement ?? null;
  }

  get inputElement(): HTMLElement | null {
    return this.inputRef?.nativeElement ?? null;
  }

  get confirmButtonElement(): HTMLElement | null {
    return this.confirmBtnRef?.nativeElement ?? null;
  }

  get denyButtonElement(): HTMLElement | null {
    return this.denyBtnRef?.nativeElement ?? null;
  }

  get cancelButtonElement(): HTMLElement | null {
    return this.cancelBtnRef?.nativeElement ?? null;
  }

  timerLeft(): number | undefined {
    return this.timerHandle ? Math.max(0, this.timerEndsAt - Date.now()) : undefined;
  }

  setLoading(loading: boolean): void {
    this.loading.set(loading);
  }

  showValidation(message: string | null): void {
    this.validationMessage.set(message && message !== '' ? message : null);
  }

  dismiss(reason?: AppDialogDismissReason): void {
    this.finish({ isConfirmed: false, isDenied: false, isDismissed: true, dismiss: reason });
  }

  // ── Buttons ─────────────────────────────────────────────────────────────────

  async confirm(): Promise<void> {
    if (this.loading() || this.closing) {
      return;
    }
    const value = appDialogConfirmValue(this.options.input, this.inputValue);
    this.validationMessage.set(null);

    if (this.options.inputValidator) {
      const message = await this.options.inputValidator(value);
      if (message) {
        this.validationMessage.set(String(message));
        return;
      }
    }

    if (!this.options.preConfirm) {
      this.finish({ isConfirmed: true, isDenied: false, isDismissed: false, value });
      return;
    }

    const outcome = await this.runHook(this.options.preConfirm, value, !!this.options.showLoaderOnConfirm);
    if (outcome) {
      this.finish({ isConfirmed: true, isDenied: false, isDismissed: false, value: outcome.value });
    }
  }

  async deny(): Promise<void> {
    if (this.loading() || this.closing) {
      return;
    }
    if (!this.options.preDeny) {
      this.finish({ isConfirmed: false, isDenied: true, isDismissed: false, value: false });
      return;
    }
    const outcome = await this.runHook(this.options.preDeny, this.inputValue, false);
    if (outcome) {
      this.finish({ isConfirmed: false, isDenied: true, isDismissed: false, value: outcome.value });
    }
  }

  onInput(): void {
    if (this.validationMessage()) {
      this.validationMessage.set(null);
    }
  }

  isPlainInput(type: string): boolean {
    return !['textarea', 'select', 'radio', 'checkbox'].includes(type);
  }

  /**
   * The page's own button class when it gave one (legacy `app-dialog-btn-*` rules), the
   * application's pill buttons otherwise.
   */
  buttonClass(kind: 'confirm' | 'deny' | 'cancel'): string {
    const custom = this.options.customClass?.[`${kind}Button` as 'confirmButton'] ?? '';
    if (custom !== '' || this.options.buttonsStyling === false) {
      return custom;
    }
    const tone = kind === 'deny' ? ' bg-error text-white' : kind === 'confirm' ? ' bg-primary text-white' : '';
    return `app-dialog-btn-default${tone}`;
  }

  size(value: string | number | undefined): string | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    return typeof value === 'number' ? `${value}px` : value;
  }

  // ── Internals ───────────────────────────────────────────────────────────────

  /**
   * preConfirm / preDeny: a thrown error or a validation message shown meanwhile keeps the dialog
   * open, like the former alert library did. Returns null when the dialog must stay.
   */
  private async runHook(hook: (value: any) => any, value: any, showLoader: boolean): Promise<{ value: any } | null> {
    if (showLoader) {
      this.loading.set(true);
    }
    let returned: any;
    try {
      returned = await hook(value);
    } catch (error: any) {
      this.loading.set(false);
      this.validationMessage.set(error?.message ? String(error.message) : 'An error occurred.');
      return null;
    }
    this.loading.set(false);
    if (this.validationMessage()) {
      return null;
    }
    const outcome = appDialogPreConfirmOutcome(returned, value);
    return outcome.stay ? null : { value: outcome.value };
  }

  private shouldConfirmOnEnter(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement | null;
    const tag = target?.tagName?.toLowerCase();
    if (tag === 'textarea' || tag === 'button' || tag === 'select' || tag === 'a') {
      return false;
    }
    if (this.options.showConfirmButton === false) {
      return false;
    }
    return appDialogAllows(this.options.allowEnterKey, true);
  }

  private finish(result: AppDialogResult): void {
    if (this.closing) {
      return;
    }
    this.closing = true;
    this.clearTimer();
    const popup = this.popupElement;
    if (popup && this.options.willClose) {
      try {
        this.options.willClose(popup);
      } catch {
        // A page hook must never keep the dialog open.
      }
    }
    this.dialogRef.close(result);
  }

  private clearTimer(): void {
    if (this.timerHandle) {
      clearTimeout(this.timerHandle);
      this.timerHandle = null;
    }
  }
}
